import type { ParsedSchema, ParsedField, TypeBoxFieldType, FieldConstraints } from './types'

/*
 * Static TypeBox source parser, ported from the Data Mapper (frontend/src/services/typeboxParser.ts).
 * User schema code is NEVER evaluated: the source is scanned (string- and comment-aware) and each
 * field's type expression is analysed from its outermost `Type.X(...)` call inward. No `eval`, no
 * `Function`, no dynamic import (typeboxParser.test.ts pins this). The only regexes built from the
 * user's text take an identifier already checked against /^[A-Za-z_$][\w$]*$/ (resolveEnumValues).
 *
 * shortcut: a COPY of packages/api/src/services/data-mapper/transform/typeboxParser.ts, which is
 * the one an import uses (the import job is not wired yet; when it is, the API's copy is the
 * authority). The images build per package, so the web cannot import across packages. The only
 * change here is that parseTypeBoxSchema is synchronous (the API's is async for no reason the
 * browser needs). Fix a parser bug in BOTH copies; promote it to a shared package if a third
 * consumer appears.
 */

/**
 * Parse TypeBox schema code and extract field definitions. Throws when no `Type.Object` schema is found.
 */
export function parseTypeBoxSchema(code: string): ParsedSchema {
  const source = stripComments(code)
  const schemaMatch = findMainSchema(source)

  if (!schemaMatch) {
    throw new Error('No valid TypeBox schema found. Expected Type.Object() definition.')
  }

  const { name, content } = schemaMatch
  const fields = parseObjectFields(content, source)

  return {
    name,
    fields,
    rawCode: code
  }
}

// ---------------------------------------------------------------------------
// Lexical helpers
// ---------------------------------------------------------------------------

const OPEN_TO_CLOSE: Record<string, string> = { '(': ')', '[': ']', '{': '}' }

/**
 * Index just past the string literal starting at `start` (a quote/backtick).
 * Backslash escapes are skipped as pairs, so `'\\'` terminates correctly.
 */
function skipString(code: string, start: number): number {
  const quote = code[start]
  let i = start + 1
  while (i < code.length) {
    const ch = code[i]
    if (ch === '\\') {
      i += 2
      continue
    }
    if (ch === quote) return i + 1
    if (quote !== '`' && ch === '\n') return i // unterminated single-line string
    i++
  }
  return code.length
}

function isQuote(ch: string | undefined): boolean {
  return ch === '"' || ch === "'" || ch === '`'
}

/**
 * Replace `//` and block comments with whitespace (newlines preserved),
 * leaving string literals untouched.
 */
export function stripComments(code: string): string {
  let out = ''
  let i = 0
  while (i < code.length) {
    const ch = code[i]
    const next = code[i + 1]

    if (isQuote(ch)) {
      const end = skipString(code, i)
      out += code.slice(i, end)
      i = end
    } else if (ch === '/' && next === '/') {
      while (i < code.length && code[i] !== '\n') i++
    } else if (ch === '/' && next === '*') {
      const end = code.indexOf('*/', i + 2)
      const stop = end === -1 ? code.length : end + 2
      out += code.slice(i, stop).replace(/[^\n]/g, ' ')
      i = stop
    } else {
      out += ch
      i++
    }
  }
  return out
}

/** Index of the bracket closing the one at `openIndex`, or -1. */
function findMatching(code: string, openIndex: number): number {
  const stack: string[] = []
  let i = openIndex
  while (i < code.length) {
    const ch = code[i]
    if (isQuote(ch)) {
      i = skipString(code, i)
      continue
    }
    if (OPEN_TO_CLOSE[ch]) {
      stack.push(OPEN_TO_CLOSE[ch])
    } else if (ch === ')' || ch === ']' || ch === '}') {
      if (stack.pop() !== ch) return -1
      if (stack.length === 0) return i
    }
    i++
  }
  return -1
}

/** Split on commas that are not nested in brackets or strings. */
function splitTopLevel(content: string): string[] {
  const result: string[] = []
  let depth = 0
  let start = 0
  let i = 0

  while (i < content.length) {
    const ch = content[i]
    if (isQuote(ch)) {
      i = skipString(content, i)
      continue
    }
    if (ch === '(' || ch === '{' || ch === '[') {
      depth++
    } else if (ch === ')' || ch === '}' || ch === ']') {
      depth--
    } else if (ch === ',' && depth === 0) {
      const part = content.slice(start, i).trim()
      if (part) result.push(part)
      start = i + 1
    }
    i++
  }

  const last = content.slice(start).trim()
  if (last) result.push(last)
  return result
}

/** Decode a JS string literal (including quotes). Returns null if `raw` is not one. */
function decodeStringLiteral(raw: string): string | null {
  const text = raw.trim()
  if (text.length < 2 || !isQuote(text[0]) || text[text.length - 1] !== text[0]) return null
  if (skipString(text, 0) !== text.length) return null
  if (text[0] === '`' && text.includes('${')) return null

  const body = text.slice(1, -1)
  let out = ''
  for (let i = 0; i < body.length; i++) {
    const ch = body[i]
    if (ch !== '\\') {
      out += ch
      continue
    }
    const esc = body[++i]
    switch (esc) {
      case 'n':
        out += '\n'
        break
      case 'r':
        out += '\r'
        break
      case 't':
        out += '\t'
        break
      case 'b':
        out += '\b'
        break
      case 'f':
        out += '\f'
        break
      case 'v':
        out += '\v'
        break
      case '0':
        out += '\0'
        break
      case 'x': {
        out += String.fromCharCode(parseInt(body.slice(i + 1, i + 3), 16))
        i += 2
        break
      }
      case 'u': {
        if (body[i + 1] === '{') {
          const close = body.indexOf('}', i)
          out += String.fromCodePoint(parseInt(body.slice(i + 2, close), 16))
          i = close
        } else {
          out += String.fromCharCode(parseInt(body.slice(i + 1, i + 5), 16))
          i += 4
        }
        break
      }
      case '\n':
        break // line continuation
      case undefined:
        break
      default:
        out += esc // \\ \' \" \/ and identity escapes
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// Schema discovery
// ---------------------------------------------------------------------------

interface SchemaMatch {
  name: string
  content: string
}

function findMainSchema(code: string): SchemaMatch | null {
  // export const UserSchema = Type.Object({   /   const userSchema: TSchema = Type.Object({
  const declarationPattern =
    /(?:export\s+)?(?:const|let|var)\s+([\w$]+)\s*(?::[^=]+)?=\s*Type\.Object\s*\(\s*\{/g

  let match: RegExpExecArray | null
  while ((match = declarationPattern.exec(code)) !== null) {
    // Skip Data, Patch, Query, etc. variants
    if (/(?:Data|Patch|Query|Params|Result)(?:Schema)?$/.test(match[1])) {
      continue
    }

    const braceIndex = match.index + match[0].length - 1
    const close = findMatching(code, braceIndex)
    if (close !== -1) {
      return {
        name: match[1].replace(/Schema$/, ''),
        content: code.slice(braceIndex + 1, close)
      }
    }
  }

  return null
}

function parseObjectFields(content: string, fullCode: string): ParsedField[] {
  const fields: ParsedField[] = []
  for (const fieldString of splitTopLevel(content)) {
    const field = parseField(fieldString, fullCode)
    if (field) fields.push(field)
  }
  return fields
}

const FIELD_PATTERN = /^\s*(?:([A-Za-z_$][\w$]*)|('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"))\s*:\s*([\s\S]+)$/

export function parseField(fieldString: string, fullCode = ''): ParsedField | null {
  const fieldMatch = fieldString.match(FIELD_PATTERN)
  if (!fieldMatch) {
    return null
  }

  const name = fieldMatch[1] ?? decodeStringLiteral(fieldMatch[2])
  if (name === null || name === undefined) return null

  return { name, ...analyzeTypeExpression(fieldMatch[3].trim(), fullCode) }
}

// ---------------------------------------------------------------------------
// Type expression analysis
// ---------------------------------------------------------------------------

interface TypeCall {
  name: string
  args: string[]
}

/** Parse `Type.Name(args)` when the call spans the whole expression. */
function parseOuterCall(expr: string): TypeCall | null {
  const text = expr.trim()
  const head = text.match(/^Type\s*\.\s*(\w+)\s*(?:<[^()]*>)?\s*\(/)
  if (!head) return null
  const open = head[0].length - 1
  const close = findMatching(text, open)
  if (close === -1 || text.slice(close + 1).trim() !== '') return null
  return { name: head[1], args: splitTopLevel(text.slice(open + 1, close)) }
}

/** Elements of an array literal `[a, b]`, or null. */
function parseArrayLiteral(expr: string): string[] | null {
  const text = expr.trim()
  if (!text.startsWith('[')) return null
  const close = findMatching(text, 0)
  if (close !== text.length - 1) return null
  return splitTopLevel(text.slice(1, close))
}

/** Top-level `key: value` pairs of an object literal, or null. */
function parseObjectLiteral(expr: string | undefined): Map<string, string> | null {
  if (!expr) return null
  const text = expr.trim()
  if (!text.startsWith('{')) return null
  const close = findMatching(text, 0)
  if (close !== text.length - 1) return null

  const props = new Map<string, string>()
  for (const entry of splitTopLevel(text.slice(1, close))) {
    const m = entry.match(FIELD_PATTERN)
    if (!m) continue
    const key = m[1] ?? decodeStringLiteral(m[2])
    if (key) props.set(key, m[3].trim())
  }
  return props
}

function isNullCall(expr: string): boolean {
  const call = parseOuterCall(expr)
  return call?.name === 'Null' || call?.name === 'Undefined'
}

type TypeAnalysis = Omit<ParsedField, 'name'>

const OPTIONAL_WRAPPERS = new Set(['Optional', 'ReadonlyOptional'])
const TRANSPARENT_WRAPPERS = new Set(['Readonly'])

export function analyzeTypeExpression(expression: string, fullCode = ''): TypeAnalysis {
  let expr = expression.trim()
  let isOptional = false
  let isNullable = false

  // Peel wrappers from the outside in. Only the OUTERMOST calls decide
  // optionality/nullability of this field; nested ones belong to inner types.
  for (let guard = 0; guard < 10; guard++) {
    const call = parseOuterCall(expr)
    if (!call) break

    if (OPTIONAL_WRAPPERS.has(call.name) && call.args.length >= 1) {
      isOptional = true
      expr = call.args[0]
      continue
    }
    if (TRANSPARENT_WRAPPERS.has(call.name) && call.args.length >= 1) {
      expr = call.args[0]
      continue
    }
    if (call.name === 'Nullable' && call.args.length >= 1) {
      isNullable = true
      expr = call.args[0]
      continue
    }
    if (call.name === 'Union') {
      const members = parseArrayLiteral(call.args[0] ?? '')
      if (members && members.some(isNullCall)) {
        isNullable = true
        const rest = members.filter((m) => !isNullCall(m))
        if (rest.length === 1) {
          expr = rest[0]
          continue
        }
        const options = call.args.slice(1).join(', ')
        expr = `Type.Union([${rest.join(', ')}]${options ? `, ${options}` : ''})`
      }
    }
    break
  }

  const call = parseOuterCall(expr)
  const constraints: FieldConstraints = {}
  let type: TypeBoxFieldType = 'unknown'
  let options: Map<string, string> | null = null

  if (call) {
    switch (call.name) {
      case 'String':
        options = parseObjectLiteral(call.args[0])
        type = /^['"`]date(?:-time)?['"`]$/.test(options?.get('format') ?? '') ? 'date' : 'string'
        break
      case 'Date':
        type = 'date'
        options = parseObjectLiteral(call.args[0])
        break
      case 'Number':
        type = 'number'
        options = parseObjectLiteral(call.args[0])
        break
      case 'Integer':
        type = 'integer'
        options = parseObjectLiteral(call.args[0])
        break
      case 'Boolean':
        type = 'boolean'
        options = parseObjectLiteral(call.args[0])
        break
      case 'Array': {
        type = 'array'
        options = parseObjectLiteral(call.args[1])
        if (call.args[0]) {
          constraints.items = { name: 'items', ...analyzeTypeExpression(call.args[0], fullCode) }
        }
        break
      }
      case 'Object':
        type = 'object'
        options = parseObjectLiteral(call.args[1])
        break
      case 'Literal': {
        type = 'literal'
        options = parseObjectLiteral(call.args[1])
        const literal = decodeStringLiteral(call.args[0] ?? '')
        if (literal !== null) constraints.enum = [literal]
        break
      }
      case 'Union': {
        type = 'union'
        options = parseObjectLiteral(call.args[1])
        const members = parseArrayLiteral(call.args[0] ?? '') ?? []
        const literals = members.map((m) => {
          const inner = parseOuterCall(m)
          return inner?.name === 'Literal' ? decodeStringLiteral(inner.args[0] ?? '') : null
        })
        if (literals.length > 0 && literals.every((l): l is string => l !== null)) {
          constraints.enum = literals
        }
        break
      }
      case 'Enum': {
        type = 'enum'
        options = parseObjectLiteral(call.args[1])
        const values = resolveEnumValues(call.args[0] ?? '', fullCode)
        if (values) constraints.enum = values
        break
      }
      default:
        type = 'unknown'
    }
  }

  if (options) {
    const opts = options
    const intOpt = (key: string) => {
      const raw = opts.get(key)
      return raw !== undefined && /^\d+$/.test(raw) ? parseInt(raw, 10) : undefined
    }
    const numOpt = (key: string) => {
      const raw = opts.get(key)
      if (raw === undefined) return undefined
      const n = Number(raw)
      return raw.trim() !== '' && Number.isFinite(n) ? n : undefined
    }

    const minLength = intOpt('minLength')
    if (minLength !== undefined) constraints.minLength = minLength
    const maxLength = intOpt('maxLength')
    if (maxLength !== undefined) constraints.maxLength = maxLength
    const minimum = numOpt('minimum')
    if (minimum !== undefined) constraints.minimum = minimum
    const maximum = numOpt('maximum')
    if (maximum !== undefined) constraints.maximum = maximum

    const patternRaw = opts.get('pattern')
    if (patternRaw !== undefined) {
      constraints.pattern = decodeStringLiteral(patternRaw) ?? regexLiteralSource(patternRaw) ?? undefined
      if (constraints.pattern === undefined) delete constraints.pattern
    }
  }

  const descriptionRaw = options?.get('description')
  const formatRaw = options?.get('format')
  const defaultRaw = options?.get('default')
  const description =
    descriptionRaw !== undefined ? (decodeStringLiteral(descriptionRaw) ?? undefined) : undefined
  const format = formatRaw !== undefined ? (decodeStringLiteral(formatRaw) ?? undefined) : undefined
  const defaultValue = defaultRaw !== undefined ? parseDefaultValue(defaultRaw) : undefined

  return {
    type,
    isOptional,
    isNullable,
    constraints,
    description,
    format,
    default: defaultValue
  }
}

function regexLiteralSource(raw: string): string | null {
  const m = raw.trim().match(/^\/((?:[^/\\\n]|\\.)+)\/[a-z]*$/)
  return m ? m[1] : null
}

/** Resolve string values of `enum Name {...}` or `const Name = {...}` without evaluating code. */
function resolveEnumValues(identifier: string, fullCode: string): string[] | null {
  const id = identifier.trim()
  if (!/^[A-Za-z_$][\w$]*$/.test(id) || !fullCode) return null
  const escaped = id.replace(/\$/g, '\\$')

  const enumDecl = new RegExp(`\\benum\\s+${escaped}\\s*\\{`).exec(fullCode)
  if (enumDecl) {
    const open = enumDecl.index + enumDecl[0].length - 1
    const close = findMatching(fullCode, open)
    if (close === -1) return null
    const values: string[] = []
    for (const member of splitTopLevel(fullCode.slice(open + 1, close))) {
      const [, init] = member.split(/=(.*)/s)
      const decoded = init !== undefined ? decodeStringLiteral(init) : null
      if (decoded === null) return null // numeric / computed members: not representable
      values.push(decoded)
    }
    return values
  }

  const constDecl = new RegExp(`\\b(?:const|let|var)\\s+${escaped}\\s*(?::[^=]+)?=\\s*\\{`).exec(fullCode)
  if (constDecl) {
    const open = constDecl.index + constDecl[0].length - 1
    const close = findMatching(fullCode, open)
    if (close === -1) return null
    const props = parseObjectLiteral(fullCode.slice(open, close + 1))
    if (!props) return null
    const values: string[] = []
    for (const raw of props.values()) {
      const decoded = decodeStringLiteral(raw.replace(/\s+as\s+const\s*$/, ''))
      if (decoded === null) return null
      values.push(decoded)
    }
    return values
  }

  return null
}

function parseDefaultValue(raw: string): unknown {
  const value = raw.trim()

  if (value === 'null') return null
  if (value === 'undefined') return undefined
  if (value === 'true') return true
  if (value === 'false') return false
  if (/^-?\d+$/.test(value)) return parseInt(value, 10)
  if (/^-?\d*\.\d+$/.test(value)) return parseFloat(value)
  const str = decodeStringLiteral(value)
  if (str !== null) return str

  return value
}

/**
 * Generate a sample TypeBox schema string for testing
 */
export function generateSampleSchema(): string {
  return `import { Type } from '@sinclair/typebox';

export const UserSchema = Type.Object({
  id: Type.Integer(),
  email: Type.String({ format: 'email' }),
  name: Type.String({ minLength: 1, maxLength: 100 }),
  age: Type.Optional(Type.Integer({ minimum: 0, maximum: 150 })),
  role: Type.Union([
    Type.Literal('admin'),
    Type.Literal('user'),
    Type.Literal('guest')
  ]),
  isActive: Type.Boolean({ default: true }),
  createdAt: Type.String({ format: 'date-time' }),
  metadata: Type.Optional(Type.Object({
    source: Type.String(),
    version: Type.Integer()
  }))
});
`
}
